import type { NewUserQuizPriority } from '@/shared/api/newUserQuiz';

import type { NewUserQuizCopy } from './i18n';
import type { NewUserQuizFormState, QuizQuestion } from './types';

export const NEW_USER_QUIZ_AUTO_ADVANCE_MS = 300;
export const NEW_USER_QUIZ_BYPASS_TTL_MS = 24 * 60 * 60 * 1000;
export const NEW_USER_QUIZ_MAX_SUBMIT_ATTEMPTS = 3;
export const NEW_USER_QUIZ_BYPASS_STORAGE_KEY = 'newUserQuiz.bypassedAt';

export const NEW_USER_QUIZ_INITIAL_STATE: NewUserQuizFormState = {
  content_team_size: null,
  localization_frequency: null,
  priorities: [],
};

export const getNewUserQuizQuestions = (copy: NewUserQuizCopy): readonly QuizQuestion[] => [
  {
    ...copy.questions.localization_frequency,
    key: 'localization_frequency',
    options: [
      { ...copy.questions.localization_frequency.options.one_time_project, value: 'one_time_project' },
      { ...copy.questions.localization_frequency.options.occasionally_quarterly, value: 'occasionally_quarterly' },
      { ...copy.questions.localization_frequency.options.continuously, value: 'continuously' },
    ],
  },
  {
    ...copy.questions.content_team_size,
    key: 'content_team_size',
    options: [
      { ...copy.questions.content_team_size.options.just_me, value: 'just_me' },
      { ...copy.questions.content_team_size.options.small_team_2_5, value: 'small_team_2_5' },
      { ...copy.questions.content_team_size.options.large_team_5_plus, value: 'large_team_5_plus' },
    ],
  },
];

export const getNewUserQuizPriorityOptions = (
  copy: NewUserQuizCopy,
): readonly {
  label: string;
  value: NewUserQuizPriority;
}[] => [
  { ...copy.priorityQuestion.options.fast_results_no_setup, value: 'fast_results_no_setup' },
  {
    ...copy.priorityQuestion.options.collaborate_with_team_or_reviewers,
    value: 'collaborate_with_team_or_reviewers',
  },
  {
    ...copy.priorityQuestion.options.integration_into_product_or_workflow,
    value: 'integration_into_product_or_workflow',
  },
];

export const NEW_USER_QUIZ_TOTAL_STEPS = 3;

export const NEW_USER_QUIZ_TEST_IDS = {
  back: 'new-user-quiz-back',
  backToVideo: 'new-user-quiz-back-to-video',
  bookDemo: 'new-user-quiz-book-demo',
  bookedBanner: 'new-user-quiz-booked-banner',
  bookedCall: 'new-user-quiz-booked-call',
  bookedCallTime: 'new-user-quiz-booked-call-time',
  bookedFollowUp: 'new-user-quiz-booked-follow-up',
  bookedManageBooking: 'new-user-quiz-booked-manage-booking',
  bookedManageBookingClose: 'new-user-quiz-booked-manage-booking-close',
  bookedManageBookingGoogle: 'new-user-quiz-booked-manage-booking-google',
  bookedManageBookingModal: 'new-user-quiz-booked-manage-booking-modal',
  bookedManageBookingOutlook: 'new-user-quiz-booked-manage-booking-outlook',
  bookedPrep: 'new-user-quiz-booked-prep',
  bookedShare: 'new-user-quiz-booked-share',
  booking: 'new-user-quiz-booking',
  bookingChoice: 'new-user-quiz-booking-choice',
  bookingConfirmed: 'new-user-quiz-booking-confirmed',
  bookingContinue: 'new-user-quiz-booking-continue',
  continue: 'new-user-quiz-continue',
  continueAnyway: 'new-user-quiz-continue-anyway',
  editorChoice: 'new-user-quiz-editor-choice',
  error: 'new-user-quiz-error',
  exploreEditor: 'new-user-quiz-explore-editor',
  maybeLater: 'new-user-quiz-maybe-later',
  option: (value: boolean | string) => `new-user-quiz-option-${String(value)}`,
  quiz: 'new-user-quiz',
} as const;
